import { useEffect } from "react"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { AppShell } from "../components/layout/app-shell"
import { QueryState } from "../components/layout/query-state"
import { useSearchMarketplaces } from "../services/use-search-rankings"

/**
 * Entry point for the keyword tracker: a pass-through to a marketplace's
 * overview, never a page of its own.
 *
 * Which marketplaces are observable is the API's answer (a repo config plus a
 * capture adapter), so this lands on the one flagged `primary` rather than naming
 * an id — and on it by name, not by list position, so a change to the API's sort
 * cannot silently move where the tracker opens. Switching between them belongs on
 * the overview itself: a chooser page costs two clicks to reach any table, and
 * once inside one marketplace there is no way to the other without leaving the
 * tracker.
 */
function SearchMarketplacesPage() {
  const marketplaces = useSearchMarketplaces()
  const navigate = useNavigate()
  const landing =
    marketplaces.data?.find((marketplace) => marketplace.primary) ?? marketplaces.data?.[0]

  useEffect(() => {
    if (landing) {
      void navigate({
        to: "/keywords/$marketplace",
        params: { marketplace: landing.id },
        replace: true,
      })
    }
  }, [landing, navigate])

  return (
    <AppShell title="Keywords" description="Marketplace search-position tracking">
      <QueryState
        // Redirecting counts as still loading: rendering an empty shell in the
        // frame before the navigation lands reads as "no data".
        isLoading={marketplaces.isLoading || landing !== undefined}
        error={marketplaces.error}
        isEmpty={marketplaces.data?.length === 0}
        emptyMessage="No search marketplace is configured yet."
      >
        {null}
      </QueryState>
    </AppShell>
  )
}

export const Route = createFileRoute("/keywords/")({ component: SearchMarketplacesPage })
